import { useQuery } from '@tanstack/react-query'
import {
  CalendarClock,
  CheckCircle2,
  Eye,
  FilePen,
  Megaphone,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Send,
  TimerOff,
  Trash2,
  Undo2,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { useSearchParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { StatCard } from '@/components/ui/StatCard'
import {
  announcementStatsQuery,
  announcementsQuery,
  useAnnouncementAction,
  useDeleteAnnouncement,
  usePinAnnouncement,
  type LifecycleAction,
} from '@/features/announcements/api'
import { AnnouncementEditor } from '@/features/announcements/components/AnnouncementEditor'
import { CategoryChip } from '@/features/announcements/components/CategoryChip'
import { audienceLabel, categoryMeta, priorityMeta, statusMeta } from '@/features/announcements/meta'
import {
  ANNOUNCEMENT_AUDIENCES,
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_STATUSES,
  type Announcement,
} from '@/features/announcements/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import type { SortDirection } from '@/types/api'
import { formatDateTime, formatNumber } from '@/utils/format'

const FILTERS = ['status', 'category', 'audience'] as const
/** English toast titles (translation keys, ns `announcements`); translated at the call site. */
const ACTION_TOAST: Record<LifecycleAction, string> = {
  publish: 'Published',
  unpublish: 'Moved back to draft',
  expire: 'Expired',
}

export function AnnouncementsAdminPage() {
  const { t } = useTranslation('announcements')
  const withGuardians = useGuardians()
  useDocumentTitle('Announcements')
  const { state, query, update, setPage } = useListParams(FILTERS, { sort: 'createdAt' })
  const [params] = useSearchParams()
  // Newest first unless the user picked a direction.
  const direction: SortDirection = params.has('direction') ? state.direction : 'desc'
  const toggleSort = (key: string) =>
    update({ sort: key, direction: state.sort === key && direction === 'asc' ? 'desc' : 'asc' })
  const list = useQuery(announcementsQuery({ ...query, direction }))
  const stats = useQuery(announcementStatsQuery())
  const [editing, setEditing] = useState<Announcement | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Announcement | null>(null)
  const action = useAnnouncementAction()
  const pin = usePinAnnouncement()
  const remove = useDeleteAnnouncement()

  const run = (a: Announcement, act: LifecycleAction) =>
    action.mutate(
      { id: a.id, action: act },
      {
        onSuccess: () => toast.success(t(ACTION_TOAST[act]), a.title),
        onError: (e) => toast.error(t('Could not update'), errorMessage(e)),
      },
    )
  const togglePin = (a: Announcement) =>
    pin.mutate(
      { id: a.id, isPinned: !a.isPinned },
      {
        onSuccess: () => toast.success(a.isPinned ? t('Unpinned') : t('Pinned'), a.title),
        onError: (e) => toast.error(t('Could not update'), errorMessage(e)),
      },
    )

  const rowActions = (a: Announcement) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          label={t('Actions for {{title}}', { title: a.title })}
          icon={<MoreHorizontal />}
          size="icon-sm"
          onClick={(e) => e.stopPropagation()}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem icon={<Pencil />} onSelect={() => setEditing(a)}>
          {t('Edit')}
        </DropdownMenuItem>
        {a.status !== 'published' ? (
          <DropdownMenuItem icon={<Send />} onSelect={() => run(a, 'publish')}>
            {t('Publish now')}
          </DropdownMenuItem>
        ) : null}
        {a.status === 'published' ? (
          <DropdownMenuItem icon={<TimerOff />} onSelect={() => run(a, 'expire')}>
            {t('Expire now')}
          </DropdownMenuItem>
        ) : null}
        {a.status !== 'draft' ? (
          <DropdownMenuItem icon={<Undo2 />} onSelect={() => run(a, 'unpublish')}>
            {t('Unpublish (to draft)')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem icon={a.isPinned ? <PinOff /> : <Pin />} onSelect={() => togglePin(a)}>
          {a.isPinned ? t('Unpin') : t('Pin to top')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Trash2 />} tone="danger" onSelect={() => setDeleting(a)}>
          {t('Delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const columns: Column<Announcement>[] = [
    {
      key: 'title',
      header: t('Announcement'),
      sortKey: 'title',
      cell: (a) => (
        <div className="flex min-w-0 items-start gap-2">
          {a.isPinned ? (
            <Pin className="mt-0.5 size-3.5 shrink-0 text-brand-600" aria-label={t('Pinned')} />
          ) : null}
          <div className="min-w-0">
            <p className="max-w-md truncate font-medium text-ink">{a.title}</p>
            <p className="max-w-md truncate text-xs text-muted">{a.body}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: t('Category'),
      sortKey: 'category',
      hideBelow: 'lg',
      cell: (a) => <CategoryChip category={a.category} />,
    },
    {
      key: 'audience',
      header: t('Audience'),
      hideBelow: 'lg',
      cell: (a) => (
        <span title={a.classes.map((c) => c.name).join(', ') || undefined}>
          {a.audience === 'class'
            ? t('{{count}} classes', { count: a.classes.length })
            : t(audienceLabel[a.audience])}
        </span>
      ),
    },
    {
      key: 'priority',
      header: t('Priority'),
      sortKey: 'priority',
      hideBelow: '2xl',
      cell: (a) =>
        a.priority === 'normal' ? (
          <span className="text-muted">{t('Normal')}</span>
        ) : (
          <Badge tone={priorityMeta[a.priority].tone} size="sm">
            {t(priorityMeta[a.priority].label)}
          </Badge>
        ),
    },
    {
      key: 'publishAt',
      header: t('Publish window'),
      sortKey: 'publishAt',
      hideBelow: 'md',
      cell: (a) => (
        <div className="text-xs">
          <span className="block text-ink-soft">
            {a.publishAt ? formatDateTime(a.publishAt) : t('Not scheduled')}
          </span>
          {a.expireAt ? (
            <span className="block text-muted">
              {t('until {{date}}', { date: formatDateTime(a.expireAt) })}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'reads',
      header: t('Reads'),
      align: 'right',
      hideBelow: '2xl',
      cell: (a) => <span className="tabular">{formatNumber(a.readCount)}</span>,
    },
    {
      key: 'status',
      header: t('Status'),
      cell: (a) => (
        <Badge tone={statusMeta[a.status].tone} dot size="sm">
          {t(statusMeta[a.status].label)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: rowActions,
    },
  ]

  const hasFilters = Boolean(state.search || Object.keys(state.filters).length)
  const statCards = [
    { status: 'published', label: 'Live now', icon: <CheckCircle2 />, tone: 'success' },
    { status: 'scheduled', label: 'Scheduled', icon: <CalendarClock />, tone: 'info' },
    { status: 'draft', label: 'Drafts', icon: <FilePen />, tone: 'brand' },
    { status: 'expired', label: 'Expired', icon: <TimerOff />, tone: 'warning' },
  ] as const

  return (
    <>
      <PageHeader
        title={t('Announcements')}
        description={
          withGuardians
            ? t(
                'Publish notices to staff, students, parents or specific classes. Status is derived from the publish window.',
              )
            : t(
                'Publish notices to staff, students or specific classes. Status is derived from the publish window.',
              )
        }
        breadcrumbs={[{ label: t('Communication') }, { label: t('Announcements') }]}
        homeHref="/admin"
        actions={
          <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
            {t('New announcement')}
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((s) => (
          <button
            key={s.status}
            type="button"
            className="rounded-card text-left"
            aria-pressed={state.filters.status === s.status}
            onClick={() => update({ status: state.filters.status === s.status ? undefined : s.status })}
          >
            <StatCard
              label={t(s.label)}
              value={stats.data?.[s.status] ?? 0}
              icon={s.icon}
              tone={s.tone}
              loading={stats.isPending}
            />
          </button>
        ))}
      </div>

      <Card>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({ search: undefined, status: undefined, category: undefined, audience: undefined })
                }
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Search title or text')}
            label={t('Search announcements')}
          />
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('Any status')}
            value={state.filters.status ?? ''}
            onChange={(e) => update({ status: e.target.value })}
            options={ANNOUNCEMENT_STATUSES.map((s) => ({ value: s, label: t(statusMeta[s].label) }))}
          />
          <Select
            aria-label={t('Filter by category')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('All categories')}
            value={state.filters.category ?? ''}
            onChange={(e) => update({ category: e.target.value })}
            options={ANNOUNCEMENT_CATEGORIES.map((c) => ({ value: c, label: t(categoryMeta[c].label) }))}
          />
          <Select
            aria-label={t('Filter by audience')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('Any audience')}
            value={state.filters.audience ?? ''}
            onChange={(e) => update({ audience: e.target.value })}
            options={ANNOUNCEMENT_AUDIENCES.map((a) => ({ value: a, label: t(audienceLabel[a]) }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Announcements')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(a) => a.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction }}
          onSortChange={toggleSort}
          onRowClick={(a) => setEditing(a)}
          mobileCard={(a) => (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate font-medium text-ink">
                  {a.isPinned ? (
                    <Pin className="size-3.5 shrink-0 text-brand-600" aria-label={t('Pinned')} />
                  ) : null}
                  {a.title}
                </p>
                <p className="mt-0.5 truncate text-xs text-muted">
                  {t(categoryMeta[a.category].label)} ·{' '}
                  {a.audience === 'class'
                    ? a.classes.map((c) => c.name).join(', ')
                    : t(audienceLabel[a.audience])}{' '}
                  · {a.publishAt ? formatDateTime(a.publishAt) : t('Draft')}
                </p>
              </div>
              <Badge tone={statusMeta[a.status].tone} size="sm">
                {t(statusMeta[a.status].label)}
              </Badge>
            </div>
          )}
          empty={
            <EmptyState
              icon={<Megaphone />}
              title={hasFilters ? t('No announcements match these filters') : t('No announcements yet')}
              description={
                hasFilters
                  ? t('Try a different search or clear the filters.')
                  : withGuardians
                    ? t('Create your first notice for staff, students or families.')
                    : t('Create your first notice for staff or students.')
              }
              action={
                hasFilters ? undefined : (
                  <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
                    {t('New announcement')}
                  </Button>
                )
              }
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>

      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted">
        <Eye className="size-3.5" aria-hidden="true" />{' '}
        {t('Reads count people who opened the notice in their feed.')}
      </p>

      {editing ? (
        <AnnouncementEditor
          open
          onOpenChange={(o) => (o ? undefined : setEditing(null))}
          announcement={editing === 'new' ? null : editing}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => (o ? undefined : setDeleting(null))}
          title={t('Delete this announcement?')}
          description={t('“{{title}}” and its read receipts will be removed permanently.', {
            title: deleting.title,
          })}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t('Announcement deleted'), deleting.title)
                setDeleting(null)
              },
              onError: (e) => toast.error(t('Could not delete'), errorMessage(e)),
            })
          }
        />
      ) : null}
    </>
  )
}
